import { VideoClient, VideoClientConfig, VideoProviderType } from '../types/video-provider'
import { VanillaWebRTCClient } from '../clients/VanillaWebRTCClient'
import { getZoomClientInstance } from '../clients/ZoomClient'
// ZoomClient singleton is imported at top level, but only instantiated when needed

/**
 * Video Client Factory (Frontend)
 * Creates instances of video clients based on configuration
 * 
 * This is the FRONTEND equivalent of VideoProviderFactory (backend)
 */
export class VideoClientFactory {
  /**
   * Create a video client instance
   * @param type - Provider type ('vanilla-webrtc', 'zoom', 'chime')
   * @param config - Client configuration
   * @returns Promise<VideoClient> instance
   */
  static async createClient(type: VideoProviderType, config: VideoClientConfig): Promise<VideoClient> {
    console.log('[VideoClientFactory] Creating client', { type })

    switch (type) {
      case 'vanilla-webrtc':
        return new VanillaWebRTCClient()
      
      case 'zoom': {
        // Use ZoomClient singleton (already imported at top)
        console.log('[VideoClientFactory] Using ZoomClient singleton')
        return getZoomClientInstance()
      }
      
      case 'chime':
        throw new Error('AWS Chime client is not yet implemented. Please use "vanilla-webrtc" or "zoom" provider.')
      
      default:
        throw new Error(`Unknown video provider type: ${type}. Supported types: vanilla-webrtc, zoom, chime`)
    }
  }

  /**
   * Create a client from environment variables
   * Reads VITE_VIDEO_PROVIDER from environment
   * @param config - Client configuration
   * @returns Promise<VideoClient> instance
   */
  static async createFromEnvironment(config: VideoClientConfig): Promise<VideoClient> {
    const providerType = (import.meta as any).env?.VITE_VIDEO_PROVIDER as VideoProviderType

    if (!providerType) {
      throw new Error('VITE_VIDEO_PROVIDER environment variable is not set. Please configure video provider.')
    }

    console.log('[VideoClientFactory] Creating client from environment', { 
      type: providerType,
      apiUrl: config.apiUrl
    })

    return this.createClient(providerType, config)
  }

  /**
   * Detect provider from session credentials
   * Used when API returns session with provider info
   * @param providerName - Provider name from API ('zoom', 'vanilla-webrtc', etc.)
   * @param config - Client configuration
   * @returns Promise<VideoClient> instance
   */
  static async createFromSession(providerName: string | undefined, config: VideoClientConfig): Promise<VideoClient> {
    if (!providerName) {
      throw new Error('No video provider specified in session. Session data is invalid.')
    }

    const type = providerName as VideoProviderType
    
    console.log('[VideoClientFactory] Creating client from session', { 
      provider: type
    })

    return this.createClient(type, config)
  }
}
